import { FormSectionHandler } from '../entity.handler';
import { AssetResponse, AssetUpdateRequest, IdName } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField, IdNameComboValueHandlers, FormBuilderKeyValue } from 'app/shared/components/form-builder';
import { EntityUtils, NumberField, ComboField } from '../utils';
import { nullAccessDenied } from 'app/services';

interface EditorValues {
    overspeed?: number;
    overspeedProfile?: IdName;
    overspeedTolerance?: number;
    excessiveIdle?: number;
    maxTripDistance?: number;
    maxTripTime?: number;
    maxInactiveTime?: number;
}

export class AssetEventsSectionHandler implements FormSectionHandler<AssetResponse, EditorValues> {

    type = 'form';
    icon = 'tachometer-alt-slow';
    bulk = true;

    title = this.translate('TITLE');
    description = this.translate('DESC');

    properties = ['overspeed', 'overspeedTolerance', 'excessiveIdle', 'overspeedProfile', 'maxTripDistance', 'maxTripTime', 'maxInactiveTime'];

    constructor(
        public admin: AdminEditorService,
        public ownerId: string
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.ASSET.EVENTS.${key}`, values);
    }

    applyUpdate(entity: AssetResponse, payload: AssetUpdateRequest): Promise<AssetResponse> {
        return this.admin.applyUpdate(api => api.entities.updateAsset(entity.id, payload));
    }

    mapEntityToEditor(entity: AssetResponse): Promise<EditorValues> {
        const result = EntityUtils.mergeFields<EditorValues>(this.properties, entity.parameters, {});

        result.overspeedProfile = entity.overspeedProfile ? { ...entity.overspeedProfile } : { id: '00000000-0000-0000-0000-000000000000', name: 'None' };

        if (result.overspeed) {
            result.overspeed = this.admin.units.fromBackend('speed', result.overspeed, 0);
        }

        if (result.maxTripDistance) {
            result.maxTripDistance = this.admin.units.fromBackend('distance', result.maxTripDistance, 2) as number;
        }
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: AssetUpdateRequest, values: EditorValues): Promise<AssetUpdateRequest> {
        entity.parameters = entity.parameters || {};

        entity.parameters.overspeed = values.overspeed ?? entity.parameters.overspeed;
        entity.parameters.overspeedTolerance = values.overspeedTolerance ?? entity.parameters.overspeedTolerance;
        entity.parameters.excessiveIdle = values.excessiveIdle ?? entity.parameters.excessiveIdle;
        entity.parameters.maxTripDistance = values.maxTripDistance ?? entity.parameters.maxTripDistance;
        entity.parameters.maxTripTime = values.maxTripTime ?? entity.parameters.maxTripTime;
        entity.parameters.maxInactiveTime = values.maxInactiveTime ?? entity.parameters.maxInactiveTime;

        entity.overspeedProfile = values.overspeedProfile ?? entity.overspeedProfile;

        if (entity.parameters.overspeed) {
            entity.parameters.overspeed = this.admin.units.toBackend('speed', entity.parameters.overspeed, 0) as number;
        }

        if (entity.parameters.maxTripDistance) {
            entity.parameters.maxTripDistance = this.admin.units.toBackend('distance', entity.parameters.maxTripDistance, 2) as number;
        }

        return Promise.resolve(entity);
    }

    async getFields(entity: AssetResponse): Promise<FormBuilderField[]> {

        const speedUnit = this.admin.units.format(0, 'speed').unit;

        const overspeedListResponse = await this.admin.getCached(`listOverspeedProfiles/${this.ownerId}/0/100/name/recurse=true&state=active`, api => api.entities.listOverspeedProfiles(this.ownerId, true, 0, 100, 'name', 'state=active'), 15).catch(nullAccessDenied);
        const overspeedProfiles = overspeedListResponse ? overspeedListResponse.items.sort((a, b) => a.name.localeCompare(b.name)) : [];

        const overspeedProfileFields: FormBuilderKeyValue[] = [
            {
                key: '00000000-0000-0000-0000-000000000000',
                value: this.admin.i18n.instant('SHARED.NONE'),
                fields: [
                    NumberField('overspeedTolerance', this.translate('FIELDS.SPEED_TOLERANCE'), false, 0, 100, '%'),
                ],
            },
            ...overspeedProfiles.map(x => ({ key: x.id, value: x.name, })),
        ];

        const fields = [
            NumberField('overspeed', this.translate('FIELDS.MAX_SPEED'), false, 0, 300, speedUnit),
            ComboField('overspeedProfile', this.translate('FIELDS.OVERSPEED_PROFILE'), true, overspeedProfileFields, IdNameComboValueHandlers),
            NumberField('excessiveIdle', this.translate('FIELDS.EXCESSIVE_IDLE'), false, 0, 3600, this.translate('FIELDS.MINUTES')),
            this.admin.user.owner.type === 'system' ? NumberField('maxTripDistance', this.translate('FIELDS.MAX_TRIP_DISTANCE'), false, 0, 9999, this.admin.units.unit('distance')) : null,
            this.admin.user.owner.type === 'system' ? NumberField('maxTripTime', this.translate('FIELDS.MAX_TRIP_TIME'), false, 0, 9999, this.translate('FIELDS.MINUTES')) : null,
            this.admin.user.owner.type === 'system' ? NumberField('maxInactiveTime', this.translate('FIELDS.MAX_INACTIVE_TIME'), false, 0, 9999, this.translate('FIELDS.MINUTES')) : null,
        ].filter(x => x);
        return EntityUtils.prepareBulk(!entity, this.properties, fields);

    }

}
